import { supabase } from '@/integrations/supabase/client';
import { getOwnerToken } from './blogOwner';

export interface BlogComment {
  id: string;
  post_id: string;
  parent_id: string | null;
  author_name: string;
  author_avatar_url: string | null;
  body: string;
  owner_token: string;
  user_id: string | null;
  created_at: string;
  updated_at: string;
}

export async function listComments(postId: string): Promise<BlogComment[]> {
  const { data, error } = await supabase
    .from('blog_comments')
    .select('id, post_id, parent_id, author_name, author_avatar_url, body, user_id, created_at, updated_at')
    .eq('post_id', postId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((c: any) => ({ ...c, owner_token: '' })) as BlogComment[];
}

async function currentIdentity(): Promise<{ userId: string | null; name?: string; avatar?: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { userId: null };
  // Try to enrich from profiles
  const { data: profile } = await supabase
    .from('profiles')
    .select('full_name, avatar_url')
    .eq('user_id', user.id)
    .maybeSingle();
  return {
    userId: user.id,
    name: profile?.full_name || user.user_metadata?.full_name || user.email?.split('@')[0],
    avatar: profile?.avatar_url || user.user_metadata?.avatar_url,
  };
}

export async function addComment(input: {
  postId: string;
  parentId?: string | null;
  authorName: string;
  body: string;
}) {
  const id = await currentIdentity();
  const finalName = (id.name || input.authorName || '').trim().slice(0, 80);
  const payload: any = {
    post_id: input.postId,
    parent_id: input.parentId || null,
    author_name: finalName,
    author_avatar_url: id.avatar || null,
    body: input.body.trim().slice(0, 5000),
    owner_token: getOwnerToken(),
    user_id: id.userId,
  };
  const { data, error } = await supabase
    .from('blog_comments')
    .insert(payload)
    .select('id, post_id, parent_id, author_name, author_avatar_url, body, user_id, created_at, updated_at')
    .single();
  if (error) throw error;
  return { ...(data as any), owner_token: '' } as BlogComment;
}

export async function updateComment(id: string, body: string) {
  const { error } = await supabase
    .from('blog_comments')
    .update({ body: body.trim().slice(0, 5000) })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteComment(id: string) {
  const { error } = await supabase
    .from('blog_comments')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

/** Owned by the current device or signed-in user. */
export function isOwnedComment(c: BlogComment, currentUserId?: string | null): boolean {
  if (currentUserId && c.user_id && c.user_id === currentUserId) return true;
  return c.owner_token === getOwnerToken();
}
